import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface Product {
	id: string;
	type: "box" | "bigbags" | "sacks" | "barrels" | "roll";
	name: string;
	length: number;
	width: number;
	height: number;
	weight: number;
	quantity: number;
	color: string;
}

export interface Pallet {
	type: string;
	length: number;
	width: number;
	height: number;
	depth: number;
	maxWeight: number;
	color: string;
}

export interface Group {
	id: string;
	name: string;
	products: Product[];
	pallet: Pallet;
	usePallets: boolean;
}

export interface ExtendedContainer {
	value: string;
	label: string;
	image: string;
	length: string;
	width: string;
	height: string;
	maxWeight: string;
	count: number;
	loadingRules: string;
	loadSpecificGroups: boolean;
	selectedGroups: string[];
}

export interface ExtendedTruck {
	value: string;
	label: string;
	image: string;
	length: string;
	width: string;
	height: string;
	maxWeight: string;
	count: number;
	loadingRules: string;
	loadSpecificGroups: boolean;
	selectedGroups: string[];
	axleLoad: {
		frontMaxWeight: number;
		frontOffset: number;
		rearMaxWeight: number;
		rearOffset: number;
	};
}

interface DialogStates {
	addProduct: boolean;
	unitsSettings: boolean;
	palletDimensions: boolean;
	productDialog: boolean;
	containerSelection: boolean;
	truckSelection: boolean;
}

/**
 * A placed unit inside a container. Palletised cargo comes back as a space with
 * `form: "pallet"` whose products ride in `children` (positioned locally to the
 * pallet); everything else is a leaf with `children: null`.
 */
export interface StuffingSpace {
	general: {
		itemQty: number;
		cargoVolume: number;
		cargoWeight: number;
		items: Record<string, number>;
	};
	children: StuffingSpace[] | null;
	form: string;
	uid: string;
	color: string;
	name: string;
	size: { x: number; y: number; z: number; radius?: number; height?: number };
	complex: { x: number; y: number; z: number };
	location: { x: number; y: number; z: number };
}

export interface StuffingResult {
	result: Array<{
		qty: number;
		spaces: Array<{
			general: {
				itemQty: number;
				cargoVolume: number;
				cargoWeight: number;
				items: Record<string, number>;
			};
			children: StuffingSpace[] | null;
			form: string;
			uid: string;
			color: string;
			name: string;
			size: {
				x: number;
				y: number;
				z: number;
			};
			complex: {
				x: number;
				y: number;
				z: number;
			};
			location: {
				x: number;
				y: number;
				z: number;
			};
		}>;
		attr: {
			type: string;
			volume: number;
			maxWeight: number;
		};
		general: {
			itemQty: number;
			cargoVolume: number;
			cargoWeight: number;
			items: Record<string, number>;
			volumeRatio: number;
			weightRatio: number;
		};
		uid: string;
		size: {
			x: number;
			y: number;
			z: number;
		};
	}>;
	cargoAttributes: Record<
		string,
		{
			name: string;
			color: string;
			volume: number;
			weight: number;
			type: string;
		}
	>;
	ignoredErrors: any[];
	version: string;
	requestId: string;
}

interface LoadCalculatorState {
	activeStep: number;
	groups: Group[];
	selectedContainers: ExtendedContainer[];
	selectedTrucks: ExtendedTruck[];
	automaticSelection: boolean;
	dialogs: DialogStates;
	editingGroupId: string | null;
	editingGroupName: string;
	selectedGroupId: string | null;
	editingProduct: {
		groupId: string;
		product: Product;
	} | null;
	selectedGroupForAdd: string | null;
	stuffingResult: StuffingResult | null;
	isLoadingStuffing: boolean;
}

const initialState: LoadCalculatorState = {
	activeStep: 0,
	groups: [
		{
			id: "1",
			name: "Group #1",
			usePallets: false,
			products: [
				{
					id: "1",
					type: "box",
					name: "Boxes 1",
					length: 500,
					width: 400,
					height: 300,
					weight: 10,
					quantity: 80,
					color: "#4CAF50",
				},
				{
					id: "2",
					type: "sacks",
					name: "Sacks",
					length: 1000,
					width: 450,
					height: 300,
					weight: 45,
					quantity: 100,
					color: "#E91E63",
				},
				{
					id: "3",
					type: "bigbags",
					name: "Big bags",
					length: 1000,
					width: 1000,
					height: 1000,
					weight: 900,
					quantity: 10,
					color: "#2196F3",
				},
			],
			pallet: {
				type: "Europe Pallets",
				length: 1200,
				width: 800,
				height: 2600,
				depth: 100,
				maxWeight: 1500,
				color: "#000000",
			},
		},
	],
	selectedContainers: [],
	selectedTrucks: [],
	automaticSelection: false,
	dialogs: {
		addProduct: false,
		unitsSettings: false,
		palletDimensions: false,
		productDialog: false,
		containerSelection: false,
		truckSelection: false,
	},
	editingGroupId: null,
	editingGroupName: "",
	selectedGroupId: null,
	editingProduct: null,
	selectedGroupForAdd: null,
	stuffingResult: null,
	isLoadingStuffing: false,
};

const loadCalculatorSlice = createSlice({
	name: "loadCalculator",
	initialState,
	reducers: {
		// Step navigation
		setActiveStep: (state, action: PayloadAction<number>) => {
			state.activeStep = action.payload;
		},
		nextStep: (state) => {
			if (state.activeStep < 2) {
				state.activeStep += 1;
			}
		},
		previousStep: (state) => {
			if (state.activeStep > 0) {
				state.activeStep -= 1;
			}
		},

		// Group management
		addGroup: (state) => {
			const newGroupNumber = state.groups.length + 1;
			const newGroup: Group = {
				id: Date.now().toString(),
				name: `Group #${newGroupNumber}`,
				usePallets: false,
				products: [],
				pallet: {
					type: "Europe Pallets",
					length: 1200,
					width: 800,
					height: 2600,
					depth: 100,
					maxWeight: 1500,
					color: "#000000",
				},
			};
			state.groups.push(newGroup);
		},
		deleteGroup: (state, action: PayloadAction<string>) => {
			state.groups = state.groups.filter(
				(group) => group.id !== action.payload,
			);
		},
		updateGroupName: (
			state,
			action: PayloadAction<{ groupId: string; name: string }>,
		) => {
			const group = state.groups.find((g) => g.id === action.payload.groupId);
			if (group) {
				group.name = action.payload.name;
			}
		},
		toggleUsePallets: (state, action: PayloadAction<string>) => {
			const group = state.groups.find((g) => g.id === action.payload);
			if (group) {
				group.usePallets = !group.usePallets;
			}
		},
		updatePallet: (
			state,
			action: PayloadAction<{ groupId: string; pallet: Pallet }>,
		) => {
			const group = state.groups.find((g) => g.id === action.payload.groupId);
			if (group) {
				group.pallet = action.payload.pallet;
			}
		},
		updatePalletColor: (
			state,
			action: PayloadAction<{ groupId: string; color: string }>,
		) => {
			const group = state.groups.find((g) => g.id === action.payload.groupId);
			if (group) {
				group.pallet.color = action.payload.color;
			}
		},

		// Product management
		addProduct: (
			state,
			action: PayloadAction<{ groupId: string; product: Omit<Product, "id"> }>,
		) => {
			const group = state.groups.find((g) => g.id === action.payload.groupId);
			if (group) {
				const newProduct: Product = {
					...action.payload.product,
					id: Date.now().toString(),
				};
				group.products.push(newProduct);
			}
		},
		updateProduct: (
			state,
			action: PayloadAction<{ groupId: string; product: Product }>,
		) => {
			const group = state.groups.find((g) => g.id === action.payload.groupId);
			if (group) {
				const productIndex = group.products.findIndex(
					(p) => p.id === action.payload.product.id,
				);
				if (productIndex !== -1) {
					group.products[productIndex] = action.payload.product;
				}
			}
		},
		deleteProduct: (
			state,
			action: PayloadAction<{ groupId: string; productId: string }>,
		) => {
			const group = state.groups.find((g) => g.id === action.payload.groupId);
			if (group) {
				group.products = group.products.filter(
					(p) => p.id !== action.payload.productId,
				);
			}
		},
		updateProductColor: (
			state,
			action: PayloadAction<{
				groupId: string;
				productId: string;
				color: string;
			}>,
		) => {
			const group = state.groups.find((g) => g.id === action.payload.groupId);
			if (group) {
				const product = group.products.find(
					(p) => p.id === action.payload.productId,
				);
				if (product) {
					product.color = action.payload.color;
				}
			}
		},

		// Container management
		addContainer: (state, action: PayloadAction<ExtendedContainer>) => {
			const exists = state.selectedContainers.some(
				(c) => c.value === action.payload.value,
			);
			if (!exists) {
				state.selectedContainers.push({
					...action.payload,
					selectedGroups: [],
				});
			}
		},
		removeContainer: (state, action: PayloadAction<string>) => {
			state.selectedContainers = state.selectedContainers.filter(
				(c) => c.value !== action.payload,
			);
		},
		updateContainerProperty: (
			state,
			action: PayloadAction<{
				containerValue: string;
				property: keyof ExtendedContainer;
				value: any;
			}>,
		) => {
			const container = state.selectedContainers.find(
				(c) => c.value === action.payload.containerValue,
			);
			if (container) {
				(container as any)[action.payload.property] = action.payload.value;
			}
		},
		updateContainerGroups: (
			state,
			action: PayloadAction<{ containerValue: string; groupIds: string[] }>,
		) => {
			const container = state.selectedContainers.find(
				(c) => c.value === action.payload.containerValue,
			);
			if (container) {
				container.selectedGroups = action.payload.groupIds;
			}
		},

		// Truck management
		addTruck: (state, action: PayloadAction<ExtendedTruck>) => {
			const exists = state.selectedTrucks.some(
				(t) => t.value === action.payload.value,
			);
			if (!exists) {
				state.selectedTrucks.push({
					...action.payload,
					selectedGroups: [],
					axleLoad: action.payload.axleLoad || {
						frontMaxWeight: 200,
						frontOffset: 100,
						rearMaxWeight: 300,
						rearOffset: 100,
					},
				});
			}
		},
		removeTruck: (state, action: PayloadAction<string>) => {
			state.selectedTrucks = state.selectedTrucks.filter(
				(t) => t.value !== action.payload,
			);
		},
		updateTruckProperty: (
			state,
			action: PayloadAction<{
				truckValue: string;
				property: keyof ExtendedTruck;
				value: any;
			}>,
		) => {
			const truck = state.selectedTrucks.find(
				(t) => t.value === action.payload.truckValue,
			);
			if (truck) {
				(truck as any)[action.payload.property] = action.payload.value;
			}
		},
		updateTruckGroups: (
			state,
			action: PayloadAction<{ truckValue: string; groupIds: string[] }>,
		) => {
			const truck = state.selectedTrucks.find(
				(t) => t.value === action.payload.truckValue,
			);
			if (truck) {
				truck.selectedGroups = action.payload.groupIds;
			}
		},
		updateTruckAxleLoad: (
			state,
			action: PayloadAction<{
				truckValue: string;
				axleLoad: {
					frontMaxWeight: number;
					frontOffset: number;
					rearMaxWeight: number;
					rearOffset: number;
				};
			}>,
		) => {
			const truck = state.selectedTrucks.find(
				(t) => t.value === action.payload.truckValue,
			);
			if (truck) {
				truck.axleLoad = action.payload.axleLoad;
			}
		},

		// Automatic selection
		setAutomaticSelection: (state, action: PayloadAction<boolean>) => {
			state.automaticSelection = action.payload;
		},

		// Dialog management
		openDialog: (state, action: PayloadAction<keyof DialogStates>) => {
			state.dialogs[action.payload] = true;
		},
		closeDialog: (state, action: PayloadAction<keyof DialogStates>) => {
			state.dialogs[action.payload] = false;
		},

		// Editing states
		setEditingGroup: (
			state,
			action: PayloadAction<{ groupId: string | null; name: string }>,
		) => {
			state.editingGroupId = action.payload.groupId;
			state.editingGroupName = action.payload.name;
		},
		setSelectedGroupId: (state, action: PayloadAction<string | null>) => {
			state.selectedGroupId = action.payload;
		},
		setEditingProduct: (
			state,
			action: PayloadAction<{
				groupId: string;
				product: Product;
			} | null>,
		) => {
			state.editingProduct = action.payload;
		},
		setSelectedGroupForAdd: (state, action: PayloadAction<string | null>) => {
			state.selectedGroupForAdd = action.payload;
		},

		// Stuffing result management
		setStuffingResult: (
			state,
			action: PayloadAction<StuffingResult | null>,
		) => {
			state.stuffingResult = action.payload;
		},
		setIsLoadingStuffing: (state, action: PayloadAction<boolean>) => {
			state.isLoadingStuffing = action.payload;
		},
	},
});

export const {
	// Step navigation
	setActiveStep,
	nextStep,
	previousStep,
	// Group management
	addGroup,
	deleteGroup,
	updateGroupName,
	toggleUsePallets,
	updatePallet,
	updatePalletColor,
	// Product management
	addProduct,
	updateProduct,
	deleteProduct,
	updateProductColor,
	// Container management
	addContainer,
	removeContainer,
	updateContainerProperty,
	updateContainerGroups,
	// Truck management
	addTruck,
	removeTruck,
	updateTruckProperty,
	updateTruckGroups,
	updateTruckAxleLoad,
	// Automatic selection
	setAutomaticSelection,
	// Dialog management
	openDialog,
	closeDialog,
	// Editing states
	setEditingGroup,
	setSelectedGroupId,
	setEditingProduct,
	setSelectedGroupForAdd,
	// Stuffing result
	setStuffingResult,
	setIsLoadingStuffing,
} = loadCalculatorSlice.actions;

export default loadCalculatorSlice.reducer;
